import { useEffect, useMemo, useState } from 'react'
import { Alert, Badge, EmptyState, ErrorState, Modal, Skeleton, Tabs } from '@indipia/ui-core'
import type { FieldReportItem, ProgressReportDay, TaskProgressDetail } from '../../api/taskProgressApi'
import { useTaskProgressDetail } from '../../hooks/useTaskProgress'
import { reportDayTabLabel, reportStatusLabel } from '../../constants/taskProgress.logic'
import { formatMonthDay, formatMonthDayTime } from '../PartnerCandidateItem'
import { PhotoCountLink } from './PhotoCountLink'
import { ReportCustReqSection } from './ReportCustReqSection'

// 시공 세부 내역 팝업 — 작업보고(B-09). 날짜 탭마다 진행상태·이슈·본문·사진, 하단에 검수완료 띠.
// 요약(보고 n일·검수 여부)은 상세창 요약 조회 값(summaryDays)을, 보고 내용은 progress-detail 을 쓴다.
// 고객 요청(S16) 영역: 941 V1.4 S16-4 기준으로 맨 아래에 '고객 요청 반영'(응답 customerRequests, 읽기 전용)을 둔다. 필드가 없거나 비면 숨긴다.

interface Props {
  open: boolean
  onClose: () => void
  taskId: number
  workTypeName: string
  partnerName?: string
  /** 요약 조회의 날짜별 집계(검수 일시 표기용) — 없으면 검수 띠를 그리지 않는다 */
  summaryDays?: ProgressReportDay[]
  /** 하단 '보고 n/N일'의 N(공종 작업일 수). 휴일 정보를 못 쓰면 null → N 생략 */
  workdays: number | null
}

const caption: React.CSSProperties = { fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)' }

/** 보고 1건의 상태 — 이슈 유형이 있으면 이슈(완료+이슈도 이슈 우선), 아니면 작업완료 여부. 판정 불가면 null */
function reportItemStatus(r: FieldReportItem): 'ISSUE' | 'COMPLETED' | 'IN_PROGRESS' | null {
  if (r.issueYn === 'Y' || r.issueTypeNames.length > 0) return 'ISSUE'
  if (r.workCmplYn === 'Y') return 'COMPLETED'
  if (r.workCmplYn === 'N') return 'IN_PROGRESS'
  return null
}

const STATUS_BADGE: Record<string, 'warning' | 'success' | 'danger'> = {
  IN_PROGRESS: 'warning', COMPLETED: 'success', ISSUE: 'danger',
}

export function WorkReportDetailModal({ open, onClose, taskId, workTypeName, partnerName, summaryDays, workdays }: Props) {
  const q = useTaskProgressDetail(taskId, open)
  const days: TaskProgressDetail['reportDays'] = useMemo(() => q.data?.reportDays ?? [], [q.data])
  const [tab, setTab] = useState('')
  const [viewerOpen, setViewerOpen] = useState(false)
  // 처음 열면 가장 최근 보고일 탭
  useEffect(() => {
    if (days.length === 0) return
    if (!days.some(d => d.workDate === tab)) setTab(days[days.length - 1].workDate)
  }, [days, tab])

  const subtitle = [workTypeName, partnerName].filter(Boolean).join(' · ')
  const current = days.find(d => d.workDate === tab) ?? null
  const lastDay = summaryDays && summaryDays.length > 0 ? summaryDays[summaryDays.length - 1] : null
  const approved = lastDay?.approvalYn === 'Y'
  const countText = workdays != null ? `보고 ${days.length}/${workdays}일` : `보고 ${days.length}일`

  return (
    <Modal open={open} onClose={() => { if (!viewerOpen) onClose() }} title="작업보고" size="md">
      <div onMouseDown={e => e.stopPropagation()}>
        <div style={caption}>시공파트너 진행 내역</div>
        {subtitle && <div className="text-sm font-bold" style={{ color: 'var(--shell-on-surface)' }}>{subtitle}</div>}

        {q.isLoading && (
          <div aria-busy="true" className="mt-2 flex flex-col gap-2">
            {[0, 1, 2].map(i => <Skeleton key={i} height={28} />)}
          </div>
        )}
        {q.isError && <div className="mt-2"><ErrorState title="작업보고를 불러오지 못했어요" onRetry={() => { void q.refetch() }} /></div>}
        {q.data && days.length === 0 && <div className="mt-2"><EmptyState title="올라온 작업보고가 없어요" /></div>}

        {days.length > 0 && (
          <>
            <Tabs
              className="mt-2"
              tabs={days.map(d => ({ label: reportDayTabLabel(d.workDate), value: d.workDate }))}
              value={tab}
              onChange={setTab}
            />
            <ul className="mt-2 flex flex-col gap-2 list-none p-0 m-0">
              {(current?.reports ?? []).map(r => {
                const st = reportItemStatus(r)
                const label = reportStatusLabel(st)
                const time = formatMonthDayTime(r.reportDtm)
                return (
                  <li key={r.fieldReportId} className="rounded-xl p-3" style={{ border: 'var(--border-width-hairline) solid var(--shell-border)', background: 'var(--shell-surface)' }}>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {st && label && <Badge status={STATUS_BADGE[st]}>{label}</Badge>}
                      {r.approvalYn === 'Y' && <Badge status="done">검수완료</Badge>}
                      {r.writerName && <span style={caption}>{r.writerName}</span>}
                      {time && <span style={caption}>{time}</span>}
                    </div>
                    {r.issueTypeNames.length > 0 && (
                      <div className="mt-1.5">
                        <Alert kind="warning">{`이슈 · ${r.issueTypeNames.join(', ')}`}</Alert>
                      </div>
                    )}
                    <div className="mt-1.5 text-sm" style={{ color: 'var(--shell-on-surface)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {r.reportContent?.trim() ? r.reportContent : '-'}
                    </div>
                    <div className="mt-1.5 text-xs">
                      <PhotoCountLink photos={r.photos} count={r.photoCount} title="작업보고 사진" alt="작업보고 사진" onViewerOpenChange={setViewerOpen} />
                    </div>
                  </li>
                )
              })}
            </ul>
            {(current?.reports ?? []).map((r, _i, all) => {
              const t = formatMonthDayTime(r.reportDtm)
              return (
                <ReportCustReqSection
                  key={r.fieldReportId}
                  items={r.customerRequests}
                  heading={all.length > 1 && t ? `${t} 보고` : undefined}
                />
              )
            })}
            <div className="mt-2" style={caption}>
              {approved && lastDay
                ? `검수완료 ${formatMonthDay(lastDay.approvalDtm ?? lastDay.workDate)} · ${countText}`
                : countText}
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}
